<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useDark, useToggle } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import Dialog from 'primevue/dialog'
import Menu from 'primevue/menu'
import { useConfirm } from 'primevue/useconfirm'

import { useI18n } from 'vue-i18n'
import Logo from '@/assets/svg/logo.svg'
import LogoMobile from '@/assets/svg/logo-mobile.svg'
import Nebula from '@/assets/svg/nebula.svg'
import AppAvatar from '@/components/atoms/avatar/AppAvatar.vue'
import AppAICreditsFloatingButton from '@/components/molecules/ai/AppAICreditsFloatingButton.vue'
import SidebarItem from '@/components/atoms/sidebars/components/SidebarItem.vue'
import SidebarToggle from '@/components/atoms/sidebars/components/SidebarToggle.vue'
import { useSidebarRoutes } from '@/components/atoms/sidebars/composables/useSidebarRoutes'
import { useAuthStore } from '@/stores/useAuthStore'
import { useI18nStore } from '@/stores/useI18nStore'

const emit = defineEmits<{
  'update:expanded': [value: boolean]
}>()

const { routes } = useSidebarRoutes()
const { t } = useI18n()
const authStore = useAuthStore()
const i18nStore = useI18nStore()
const router = useRouter()
const { extension } = storeToRefs(authStore)
const confirm = useConfirm()
const isDark = useDark({ selector: 'html' })
const toggleDark = useToggle(isDark)

const userMenu = ref()
const showLanguageDialog = ref(false)
const isPinned = ref(false)
const isHovering = ref(false)
const HOVER_OPEN_DELAY = 120
const HOVER_CLOSE_DELAY = 120
let hoverOpenTimer: ReturnType<typeof setTimeout> | null = null
let hoverCloseTimer: ReturnType<typeof setTimeout> | null = null
const isDesktop = ref(true)
let mq: MediaQueryList | null = null

const floatingMenu = ref({
  visible: false,
  items: [] as { title: string; path: string }[],
  x: 0,
  y: 0,
})

let closeTimeout: ReturnType<typeof setTimeout>

const updateDesktop = () => {
  isDesktop.value = mq ? mq.matches : true
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    mq = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1024px)')
    updateDesktop()
    mq?.addEventListener('change', updateDesktop)

    try {
      const saved = localStorage.getItem('app.sidebar.pinned')
      if (saved === '1') {
        isPinned.value = true
        emit('update:expanded', true)
      } else {
        emit('update:expanded', false)
      }
    } catch {}
  }
})

onBeforeUnmount(() => {
  mq?.removeEventListener('change', updateDesktop)
  if (hoverOpenTimer) clearTimeout(hoverOpenTimer)
  if (hoverCloseTimer) clearTimeout(hoverCloseTimer)
  if (closeTimeout) clearTimeout(closeTimeout)
})

const uiExpanded = computed(() => isPinned.value || (isHovering.value && isDesktop.value))

watch(
  uiExpanded,
  (value) => {
    emit('update:expanded', value)
  },
  { immediate: true },
)

const toggleExpand = () => {
  isPinned.value = !isPinned.value
  try {
    localStorage.setItem('app.sidebar.pinned', isPinned.value ? '1' : '0')
  } catch {}
}

const onMouseEnter = () => {
  if (!isDesktop.value) return
  if (hoverCloseTimer) {
    clearTimeout(hoverCloseTimer)
    hoverCloseTimer = null
  }
  if (hoverOpenTimer) clearTimeout(hoverOpenTimer)
  hoverOpenTimer = setTimeout(() => {
    isHovering.value = true
  }, HOVER_OPEN_DELAY)
}

const onMouseLeave = () => {
  if (!isDesktop.value) return
  if (hoverOpenTimer) {
    clearTimeout(hoverOpenTimer)
    hoverOpenTimer = null
  }
  if (hoverCloseTimer) clearTimeout(hoverCloseTimer)
  hoverCloseTimer = setTimeout(() => {
    isHovering.value = false
  }, HOVER_CLOSE_DELAY)
}

const openFloatingMenu = (event: MouseEvent, children: { title: string; path: string }[]) => {
  clearTimeout(closeTimeout)
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  floatingMenu.value = {
    visible: true,
    items: children,
    x: rect.right + 8,
    y: rect.top + rect.height / 2,
  }
}

const closeFloatingMenu = () => {
  closeTimeout = setTimeout(() => {
    floatingMenu.value.visible = false
  }, 200)
}

const cancelCloseFloatingMenu = () => {
  clearTimeout(closeTimeout)
}

const toggleUserMenu = (event: Event) => {
  userMenu.value.toggle(event)
}

const userInitials = computed(() => {
  if (!extension.value?.number) return 'U'
  return extension.value.number
    .split(' ')
    .map((n: string[]) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
})

const userName = computed(() => extension.value?.number || t('common.user.default_name'))
const userEmail = computed(() => extension.value?.description || '')
const languageOptions = computed(() => [
  { name: t('common.languages.es'), value: 'es' },
  { name: t('common.languages.en'), value: 'en' },
])

const changeLanguage = (lang: 'es' | 'en') => {
  i18nStore.changeLanguage(lang)
  showLanguageDialog.value = false
}

const confirmLogout = () => {
  confirm.require({
    message: t('common.auth.sure_logout'),
    header: t('common.auth.confirm_logout'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    rejectLabel: t('common.general.cancel'),
    acceptLabel: t('common.auth.logout'),
    acceptClass: 'p-button-danger',
    accept: () => {
      authStore.logout()
    },
  })
}

const userMenuItems = computed(() => [
  {
    label: t('common.user.settings'),
    icon: 'pi pi-cog',
    command: () => router.push('/settings'),
  },
  {
    label: t('common.theme.dark'),
    icon: isDark.value ? 'pi pi-sun' : 'pi pi-moon',
    command: () => toggleDark(),
  },
  {
    separator: true,
  },
  {
    label: t('common.languages.language'),
    icon: 'pi pi-globe',
    command: () => {
      userMenu.value?.hide?.()
      showLanguageDialog.value = true
    },
  },
  {
    separator: true,
  },
  {
    label: t('common.auth.logout'),
    icon: 'pi pi-sign-out',
    command: confirmLogout,
  },
])
</script>

<template>
  <div
    class="w-16 h-screen flex flex-col fixed top-0 left-0 transition-all duration-300 ease-in-out z-70 shadow-[4px_0_10px_-3px_rgba(0,0,0,0.1)]"
    :class="[
      { 'w-50': uiExpanded },
      isDark
        ? 'bg-(--p-surface-900) text-(--p-surface-50)'
        : 'app-glass  text-(--color-primary-500)',
    ]"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="py-2 px-2 relative ">
      <router-link to="/" class="flex items-center justify-center h-16 mr-2">
        <Logo
          v-if="uiExpanded"
          :class="
            isDark
              ? 'h-7 w-auto transition-all duration-500 ease-in-out text-(--p-primary-color)'
              : 'h-7 w-auto transition-all duration-500 ease-in-out'
          "
        />
        <LogoMobile
          v-else
          :class="
            isDark
              ? 'h-8 w-auto transition-all duration-500 ease-in-out text-(--p-primary-color)'
              : 'h-8 w-auto transition-all duration-500 ease-in-out'
          "
        />
      </router-link>

      <SidebarToggle
        v-if="uiExpanded"
        :is-expanded="isPinned"
        @toggle="toggleExpand"
        class="absolute right-2 top-1/2 -translate-y-1/2"
      />
    </div>

    <div class="flex-1 flex flex-col overflow-y-auto px-2 py-3">
      <ul class="space-y-1 w-full">
        <SidebarItem
          v-for="route in routes"
          :key="route.path"
          :icon="route.icon"
          :label="route.title"
          :route="route.path"
          :is-expanded="uiExpanded"
          :is-active="$route.path.startsWith(route.path)"
          :children="route.children"
          @open-floating-menu="openFloatingMenu"
          class="mb-1"
        />
      </ul>
    </div>

    <!-- Floating Submenu -->
    <transition name="slide-fade">
      <div
        v-if="floatingMenu.visible"
        class="fixed shadow-xl rounded-lg p-2 min-w-45 z-50"
        :class="
          isDark
            ? 'bg-(--p-surface-800) text-(--p-surface-50)'
            : 'bg-(--p-surface-0) text-(--p-surface-900)'
        "
        :style="{
          left: `${floatingMenu.x}px`,
          top: `${floatingMenu.y}px`,
          transform: 'translateY(-50%)',
        }"
        @mouseenter="cancelCloseFloatingMenu"
        @mouseleave="closeFloatingMenu"
      >
        <ul class="flex flex-col space-y-1">
          <li v-for="(child, idx) in floatingMenu.items" :key="idx">
            <router-link
              :to="child.path"
              class="flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-all duration-200 hover:bg-(--p-surface-100) hover:text-(--p-primary-color)"
            >
              <div class="w-1.5 h-1.5 rounded-full bg-(--p-surface-400)"></div>
              {{ child.title }}
            </router-link>
          </li>
        </ul>
      </div>
    </transition>

    <!-- Perfil de usuario y logo Nebula -->
    <div class="relative mt-auto pb-3">
      <!-- Perfil de usuario -->
      <div
        class="flex p-3 transition-all duration-300 rounded-lg mx-2 mb-2"
        :class="[
          {
            'items-center justify-center': !uiExpanded,
            'flex-col items-start gap-2': uiExpanded,
          },
        ]"
      >
        <div
          class="flex w-full items-center"
          :class="uiExpanded ? 'justify-between' : 'justify-center'"
        >
          <div class="relative">
            <button
              type="button"
              class="cursor-pointer mb-0 rounded-full transition-colors"
              @click="toggleUserMenu"
            >
              <AppAvatar
                class="shrink-0 w-9! h-9! text-xs! ring-2"
                :class="[
                  isDark
                    ? 'bg-(--p-primary-color)! text-(--p-primary-contrast-color)! ring-(--p-primary-color)/20'
                    : 'bg-(--p-primary-color)! text-(--p-primary-contrast-color)! ring-(--p-primary-color)/20',
                ]"
                :label="userInitials"
                size="large"
              />
            </button>

            <AppAICreditsFloatingButton v-if="!uiExpanded" collapsed allow-purchase />
          </div>

          <AppAICreditsFloatingButton v-if="uiExpanded" allow-purchase />
        </div>

        <div
          v-if="uiExpanded"
          class="flex flex-col min-w-0 mt-2 transition-all duration-300 ease-in-out overflow-hidden flex-1"
        >
          <span class="text-sm font-semibold truncate">{{ userName }}</span>
          <span class="text-xs opacity-70 truncate">{{ userEmail }}</span>
        </div>
      </div>

      <!-- Menu de usuario -->
      <Menu ref="userMenu" :model="userMenuItems" :popup="true" />

      <Dialog
        v-model:visible="showLanguageDialog"
        :modal="false"
        :closable="true"
        :dismissable-mask="true"
        class="w-72"
        :header="t('common.languages.language')"
        :pt="{ root: 'language-floating-dialog' }"
      >
        <div class="flex flex-col gap-2">
          <button
            v-for="option in languageOptions"
            :key="option.value"
            type="button"
            class="flex items-center justify-between rounded-lg border border-(--p-surface-200) px-3 py-2 text-left text-sm transition hover:bg-(--p-surface-100) dark:border-(--p-surface-700) dark:text-white dark:hover:bg-(--p-surface-800)"
            :class="{
              'border-(--p-primary-color)! bg-(--p-primary-color)/10 text-(--p-primary-color)! dark:text-white!':
                i18nStore.language === option.value,
            }"
            @click="changeLanguage(option.value as 'es' | 'en')"
          >
            <span>{{ option.name }}</span>
            <i v-if="i18nStore.language === option.value" class="pi pi-check text-xs" />
          </button>
        </div>
      </Dialog>

      <!-- Logo Nebula -->
      <div
        class="flex px-2 gap-x-1"
        :class="
          uiExpanded ? 'items-center justify-start ml-3 mb-2 mr-2' : 'items-center justify-center'
        "
      >
        <Nebula
          class="h-4 transition-all duration-300"
          :class="[uiExpanded ? 'w-auto' : 'w-10', isDark ? 'text-(--p-primary-color)' : '']"
        />
        <span class="text-[8px]">2.0.8</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
  transform-origin: left center;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

:deep(.language-floating-dialog.p-dialog),
:deep(.language-floating-dialog .p-dialog-header),
:deep(.language-floating-dialog .p-dialog-content) {
  background: var(--p-surface-900);
  color: var(--p-text-color);
}
</style>
